/**
 * Dashboard Page
 * Overview statistics and metrics
 */

import { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { apiClient } from "../lib/api"
import { formatCurrency, formatNumber } from "../lib/utils"
import type { DashboardOverview } from "../types"

export function DashboardPage() {
  const [overview, setOverview] = useState<DashboardOverview | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadOverview()
  }, [])

  const loadOverview = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const data = await apiClient.getDashboardOverview()
      setOverview(data)
    } catch (err) {
      console.error("Failed to load overview:", err)
      setError(err instanceof Error ? err.message : "Failed to load dashboard data")
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <p style={{ fontSize: "1.125rem", color: "#94a3b8" }}>Loading dashboard...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: "3rem" }}>
        <p style={{ fontSize: "1.125rem", color: "#fca5a5", marginBottom: "1rem" }}>{error}</p>
        <button
          type="button"
          onClick={loadOverview}
          style={{
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            border: "none",
            borderRadius: "12px",
            color: "white",
            fontSize: "0.9375rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 0 20px rgba(139, 92, 246, 0.3)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow = "0 0 30px rgba(139, 92, 246, 0.4)"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 0 20px rgba(139, 92, 246, 0.3)"
          }}
        >
          Retry
        </button>
      </div>
    )
  }

  if (!overview) return null

  const stats = [
    {
      label: "Total Organizations",
      value: overview.totalOrganizations,
      link: "/organizations",
      color: "#007bff",
    },
    {
      label: "Active Organizations",
      value: overview.activeOrganizations,
      link: "/organizations?status=active",
      color: "#28a745",
    },
    {
      label: "Suspended Organizations",
      value: overview.suspendedOrganizations,
      link: "/organizations?status=suspended",
      color: "#dc3545",
    },
    {
      label: "Total Users",
      value: overview.totalUsers,
      color: "#6f42c1",
    },
    {
      label: "Total API Keys",
      value: overview.totalApiKeys,
      color: "#fd7e14",
    },
    {
      label: "Total Trades",
      value: overview.totalTrades,
      color: "#17a2b8",
    },
  ]

  return (
    <div style={{ padding: "2rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "2rem" }}>
        <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "#f1f5f9" }}>Dashboard Overview</h1>
        <Link to="/organizations/new">
          <button
            type="button"
            style={{
              padding: "0.75rem 1.5rem",
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              border: "none",
              borderRadius: "12px",
              color: "white",
              fontSize: "0.9375rem",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.3s ease",
              boxShadow: "0 0 20px rgba(139, 92, 246, 0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)"
              e.currentTarget.style.boxShadow = "0 0 30px rgba(139, 92, 246, 0.4)"
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)"
              e.currentTarget.style.boxShadow = "0 0 20px rgba(139, 92, 246, 0.3)"
            }}
          >
            Create Organization
          </button>
        </Link>
      </div>

      {/* Stats Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
          gap: "1.5rem",
          marginBottom: "2rem",
        }}
      >
        {stats.map((stat) => (
          <div
            key={stat.label}
            className="card"
            style={{
              borderLeft: `4px solid ${stat.color}`,
            }}
          >
            {stat.link ? (
              <Link to={stat.link} style={{ textDecoration: "none" }}>
                <p style={{ fontSize: "0.875rem", color: "#94a3b8", marginBottom: "0.5rem" }}>{stat.label}</p>
                <p style={{ fontSize: "2rem", fontWeight: 600, color: "#f1f5f9" }}>{formatNumber(stat.value)}</p>
              </Link>
            ) : (
              <>
                <p style={{ fontSize: "0.875rem", color: "#94a3b8", marginBottom: "0.5rem" }}>{stat.label}</p>
                <p style={{ fontSize: "2rem", fontWeight: 600, color: "#f1f5f9" }}>{formatNumber(stat.value)}</p>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Total Balances */}
      {overview.totalBalances && overview.totalBalances.length > 0 && (
        <div className="card">
          <h2 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1rem" }}>
            Total Balances
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
              gap: "1rem",
            }}
          >
            {overview.totalBalances.map((balance) => (
              <div key={balance.currency}>
                <p style={{ fontSize: "0.75rem", color: "#94a3b8", marginBottom: "0.25rem" }}>{balance.currency}</p>
                <p style={{ fontSize: "1.125rem", fontWeight: 600, color: "#f1f5f9" }}>
                  {formatCurrency(balance.total, balance.currency)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="card" style={{ marginTop: "1.5rem" }}>
        <h2 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#f1f5f9", marginBottom: "1rem" }}>Quick Actions</h2>
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          <Link to="/organizations/new" style={{ textDecoration: "none" }}>
            <button
              type="button"
              style={{
                padding: "0.75rem 1.5rem",
                background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                border: "none",
                borderRadius: "12px",
                color: "white",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s ease",
                boxShadow: "0 0 20px rgba(139, 92, 246, 0.3)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)"
                e.currentTarget.style.boxShadow = "0 0 30px rgba(139, 92, 246, 0.4)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)"
                e.currentTarget.style.boxShadow = "0 0 20px rgba(139, 92, 246, 0.3)"
              }}
            >
              Create Organization
            </button>
          </Link>
          <Link to="/organizations" style={{ textDecoration: "none" }}>
            <button
              type="button"
              style={{
                padding: "0.75rem 1.5rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                color: "#e2e8f0",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                e.currentTarget.style.transform = "translateY(-2px)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                e.currentTarget.style.transform = "translateY(0)"
              }}
            >
              View All Organizations
            </button>
          </Link>
          <Link to="/activity-log" style={{ textDecoration: "none" }}>
            <button
              type="button"
              style={{
                padding: "0.75rem 1.5rem",
                background: "rgba(255, 255, 255, 0.05)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                color: "#e2e8f0",
                fontSize: "0.875rem",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.08)"
                e.currentTarget.style.transform = "translateY(-2px)"
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "rgba(255, 255, 255, 0.05)"
                e.currentTarget.style.transform = "translateY(0)"
              }}
            >
              View Activity Log
            </button>
          </Link>
        </div>
      </div>
    </div>
  )
}
